/**
 * Vitesse de simulation : pause, ralenti (×¼, ×½) et accéléré jusqu'à ×32.
 * Plus lent / plus vite de part et d'autre de la vitesse courante, et un menu
 * avec toutes les vitesses. Raccourcis : Espace (pause), − et + (plus lent,
 * plus vite), 1 à 6 (×1 à ×32, touche physique). Réservé à l'hôte du serveur.
 */
import { useEffect, useState } from 'preact/hooks';
import { ChevronDown, FastForward, Pause, Play, Rewind } from 'lucide-preact';
import { SPEEDS, speedLabel, stepSpeed, type SimSpeed } from '@snowchain/shared';
import { command, hud } from './context';
import './speed.css';

/** Touches 1 à 6 (position physique) : vitesses de ×1 à ×32. */
const PRESETS: Record<string, SimSpeed> = { Digit1: 1, Digit2: 2, Digit3: 4, Digit4: 8, Digit5: 16, Digit6: 32 };
const HINT: Partial<Record<SimSpeed, string>> = { 0.25: 'ralenti', 0.5: 'ralenti', 1: 'normale', 32: 'une journée en 50 s' };

/** Dernière vitesse non nulle : reprise après la pause, base de « plus lent / plus vite » en pause. */
let lastRunning: SimSpeed = 1;

function setSpeed(v: SimSpeed): void {
  void command({ type: 'setSpeed', speed: v });
}

function current(): SimSpeed {
  const s = (hud.store?.clock.speed ?? 1) as SimSpeed;
  if (s > 0) lastRunning = s;
  return s;
}

/** Raccourci clavier de vitesse ; renvoie vrai s'il a été traité. */
export function speedHotkey(code: string): boolean {
  const speed = current();
  const preset = PRESETS[code];
  if (preset) {
    setSpeed(preset);
    return true;
  }
  if (code === 'Minus' || code === 'NumpadSubtract') {
    setSpeed(stepSpeed(speed || lastRunning, -1));
    return true;
  }
  if (code === 'Equal' || code === 'NumpadAdd') {
    setSpeed(stepSpeed(speed || lastRunning, 1));
    return true;
  }
  return false;
}

/** Pause ou reprise à la dernière vitesse. */
export function togglePause(): void {
  const speed = current();
  setSpeed(speed === 0 ? lastRunning : 0);
}

export function SpeedControl(props: { host: boolean }) {
  const speed = current();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!(e.target as HTMLElement | null)?.closest('.g-speedx')) setOpen(false);
    };
    window.addEventListener('pointerdown', close, true);
    return () => window.removeEventListener('pointerdown', close, true);
  }, [open]);
  if (!props.host) return <span class="g-sub">{speed === 0 ? 'En pause' : `Vitesse ${speedLabel(speed)}`}</span>;
  const base = speed || lastRunning;
  const min = SPEEDS[1];
  const max = SPEEDS[SPEEDS.length - 1];
  return (
    <div class="g-speedx" role="group" aria-label="Vitesse de simulation">
      <button class={`g-speedx-btn${speed === 0 ? ' on' : ''}`} aria-label={speed === 0 ? 'Reprendre' : 'Pause'} title="Pause (Espace)" onClick={togglePause}>
        {speed === 0 ? <Play size={13} /> : <Pause size={13} />}
      </button>
      <button class="g-speedx-btn" aria-label="Plus lent" title="Plus lent (−)" disabled={base <= min} onClick={() => setSpeed(stepSpeed(base, -1))}>
        <Rewind size={13} />
      </button>
      <button class={`g-speedx-value num${open ? ' open' : ''}`} aria-haspopup="menu" aria-expanded={open} title="Toutes les vitesses" onClick={() => setOpen(!open)}>
        {speed === 0 ? 'Pause' : speedLabel(speed)}
        <ChevronDown size={11} />
      </button>
      <button class="g-speedx-btn" aria-label="Plus vite" title="Plus vite (+)" disabled={base >= max} onClick={() => setSpeed(stepSpeed(base, 1))}>
        <FastForward size={13} />
      </button>
      {open && (
        <div class="g-speedx-menu" role="menu">
          {SPEEDS.filter((s) => s > 0).map((s) => {
            const key = Object.entries(PRESETS).find(([, v]) => v === s)?.[0]?.slice(5);
            return (
              <button
                key={s}
                role="menuitemradio"
                aria-checked={s === speed}
                class={s === speed ? 'on' : ''}
                onClick={() => {
                  setSpeed(s);
                  setOpen(false);
                }}
              >
                <span class="num">{speedLabel(s)}</span>
                <span class="g-speedx-hint">{HINT[s] ?? ''}</span>
                {key && <kbd>{key}</kbd>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}